'use client';

import { Text, Title } from '@mantine/core';
import React from 'react';
import { ErrorBoundary as ErrorBoundaryLib, FallbackProps } from 'react-error-boundary';

import useRollbar from '@/hooks/use-rollbar';

export type FallbackComponentProps = FallbackProps;
export type FallbackComponent = React.FC<FallbackComponentProps>;

export const DefaultErrorFallback: React.FC = () => {
  return (
    <div className="flex size-full flex-1 flex-col items-center p-4" role="alert">
      <Title className="mt-8">Não foi possível exibir o conteúdo</Title>
      <Text>Tente novamente mais tarde</Text>
    </div>
  );
};

export interface ErrorBoundaryProps {
  children: React.ReactElement | React.ReactNode;
  fallbackComponent?: React.ComponentType<FallbackProps>; // Fallback customizável
  resetKeys?: Array<unknown>; // Chaves para reset automático
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  fallbackComponent = DefaultErrorFallback,
  resetKeys,
}) => {
  const rollbar = useRollbar();

  const componentProps = React.isValidElement(children) ? children.props : {};

  // Função para logar o erro com as props e nome do componente
  function logErrorWithDetails(error: Error, info: React.ErrorInfo, componentProps: unknown) {
    const url = typeof window !== 'undefined' ? window.location.href : 'server';

    rollbar.error(error.message || 'ErrorBoundary caught an error', error, {
      componentProps,
      componentStack: info.componentStack,
      url,
    });
  }

  return (
    <ErrorBoundaryLib
      FallbackComponent={fallbackComponent}
      onError={(error, info) => logErrorWithDetails(error, info, componentProps)}
      resetKeys={resetKeys}
    >
      {children}
    </ErrorBoundaryLib>
  );
};
